import { yupResolver } from '@hookform/resolvers/yup';
import { BlogFont, BlogHomeCustomization, CustomPageType, IOperationResultNoValue, TextAttributes } from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../../queries/Attachments/useImageAttachmentMutation';
import { useUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useUpdateBlogStyleMutation';
import { ButtonBase } from '../../../../../web/components/Button';
import { ImageFileChooserModal } from '../../../../../web/components/ImageFileChooserModal';
import { Toggle } from '../../../../../web/components/Toggle';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { LabeledInput } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { blogHomeSchema } from './schemas';

const FONT_VALUES: BlogFont = {
	fontName: '',
	fontUri: '',
};
const TEXT_ATTRIBUTES: TextAttributes = {
	font: { ...FONT_VALUES },
	color: '',
	backgroundColor: '',
};
const DEFAULT_VALUES: BlogHomeCustomization = {
	pageBackgroundColor: '',
	headerImage: null,
	font: {
		fontName: '',
		fontUri: '',
	},
	blogTitle: {
		...TEXT_ATTRIBUTES,
		title: '',
	},
	cardStyle: {
		titleStyle: {
			...TEXT_ATTRIBUTES,
		},
		linkStyle: {
			...TEXT_ATTRIBUTES,
		},
		showAuthorMetaData: true,
	},
	hideNook: false,
	type: CustomPageType.BlogHome,
};

interface BlogHomeDesignFormProps {
	customContent: BlogHomeCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

export const HomeDesignForm: FC<BlogHomeDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();
	const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
	const { accountId, impersonationContext } = useAccountContext();

	const {
		handleSubmit,
		register,
		setValue,
		watch,
		formState: { errors, isDirty },
	} = useForm<BlogHomeCustomization>({
		defaultValues: DEFAULT_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(blogHomeSchema),
		values: customContent,
	});
	const metadataToggle = watch('cardStyle.showAuthorMetaData');
	const nookToggle = watch('hideNook');
	const font = watch('font', FONT_VALUES);

	const handleToggleMetadata = (value: boolean) => {
		setValue('cardStyle.showAuthorMetaData', value);
	};
	const handleNookToggle = (value: boolean) => {
		setValue('hideNook', value);
	};
	const handleSelectFont = (selectedFont: BlogFont) => {
		setValue('font', selectedFont);
	};

	const updateHomeMutation = useUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onValid: SubmitHandler<BlogHomeCustomization> = (data, event) => {
		event.preventDefault();
		event.stopPropagation();

		updateHomeMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	const imageUpload = useImageAttachmentMutation({
		onError: error => errorMessages.pushApiError(error, 'Failed to upload image'),
		onSuccess: result => {
			setValue('headerImage', result.at(0));
		},
	});

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);

	return (
		<>
			<form onSubmit={handleSubmit(onValid)}>
				<Column>
					<Row className={css(styleSheet.titleRow)}>
						<h1 className={css(styleSheet.titleText)}>Customize Blog Home Page</h1>
						<Column className={css(styleSheet.submitColumn)}>
							<input
								className={css(bs.ctaButton)}
								type='submit'
								value='Save & Update'
								disabled={updateHomeMutation.isPending || !!Object.keys(errors).length}
							/>
						</Column>
					</Row>
					<Row className={css(styleSheet.gap24)}>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Background</h2>
							<LabeledInput
								error={errors?.pageBackgroundColor}
								label='Page Background Color'
								{...register('pageBackgroundColor')}
							/>
							<Column>
								<div className={css(bs.mb4)}>
									<label className={css(styleSheet.label, styleSheet.labelRow)} htmlFor='upload-header-button'>
										Header Image
									</label>
									<ButtonBase
										className={css(bs.ctaButtonReverse, bs.whiteSpaceNoWrap, styleSheet.uploadButton)}
										id='upload-header-button'
										onClick={() => setImageModalOpen(true)}
									>
										Upload Header Image
									</ButtonBase>
								</div>

								{watch('headerImage')?.id && (
									<div>
										<label className={css(styleSheet.label, styleSheet.labelRow)} htmlFor='remove-header-button'>
											Remove Header Image
										</label>
										<ButtonBase
											className={css(
												bs.ctaButtonDestructive,
												bs.whiteSpaceNoWrap,
												styleSheet.uploadButton,
												bs.flex,
												bs.flexRow,
												bs.gap2
											)}
											id='remove-header-button'
											onClick={() => setValue('headerImage', { fileName: '', fileSize: 0, id: '', url: '' })}
										>
											<TrashIcon /> <span>Remove Header Image</span>
										</ButtonBase>
									</div>
								)}
							</Column>
						</Column>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Font</h2>
							<FontSelect
								initialValue={customContent.font}
								onChange={handleSelectFont}
								errors={errors?.font}
								value={font}
							/>
						</Column>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Section Title</h2>
							<LabeledInput error={errors?.blogTitle?.title} label='Title Text' {...register('blogTitle.title')} />
							<LabeledInput error={errors?.blogTitle?.color} label='Color' {...register('blogTitle.color')} />
							<Column>
								<Row className={css(bs.itemsCenter, styleSheet.labelRow)}>
									<label htmlFor='nook-toggle' className={css(styleSheet.label)}>
										Hide Nook
									</label>
									<InfoPopover message='This setting will be used for the blog home page, as well as individual posts' />
								</Row>
								<Toggle id='nook-toggle' isOn={nookToggle} onToggleCheckChanged={handleNookToggle} />
							</Column>
						</Column>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Individual Post</h2>
							<LabeledInput
								error={errors?.cardStyle?.titleStyle?.color}
								label='Title Color'
								{...register('cardStyle.titleStyle.color')}
							/>
							<LabeledInput
								error={errors?.cardStyle?.linkStyle?.color}
								label='Link Color'
								{...register('cardStyle.linkStyle.color')}
							/>
							<Column>
								<label htmlFor='metadata-toggle' className={css(styleSheet.label, styleSheet.labelRow)}>
									Show Author Metadata
								</label>
								<Toggle id='metadata-toggle' isOn={metadataToggle} onToggleCheckChanged={handleToggleMetadata} />
							</Column>
						</Column>
						<Column className={css(bs.flex1)} />
					</Row>
				</Column>
			</form>
			<ImageFileChooserModal
				title='Upload Header Image'
				modalProps={{
					isOpen: imageModalOpen,
					onRequestClose: (result, cancel) => {
						if (!cancel && result) imageUpload.mutate({ files: [result], impersonationContext });
						setImageModalOpen(false);
					},
				}}
			/>
		</>
	);
};
